import React, { Component, KeyboardEvent, MouseEvent, WheelEvent } from "react";

import { CONST_ARROW_DOWN, CONST_ARROW_LEFT, CONST_ARROW_RIGHT, CONST_ARROW_UP, CONST_LEFT_MOUSE_BTN, CONST_MAX_SCALE_INDEX, CONST_MIDDLE_MOUSE_BTN, CONST_MIN_SCALE_INDEX, CONST_NO_MOUSE_BTN, CONST_RIGHT_MOUSE_BTN, CONST_ROOT_COMPONENT_INDEX, PAINTINGS_PREVENTED_KEYS, PICASSO_PAINTING_BOX_CSS_CLASS, PICASSO_PAINTING_CSS_CLASS, PICASSO_PAINTING_WRAPPER_CSS_CLASS, TAB_INDEX_FOCUSABLE_TAB_SEQUENCE } from "../constants";
import { AlignType } from "../enums";
import { IComponent, ILink, IPoint } from "../interfaces";
import { ComponentChangeType, EmptyObjectType, LinkChangeType, LinkPassableProps, MoveablePassableProps, PicassoEditorProps, ScrollPosition, SelectablePoint, SelectedAnchorsType, WrapMapStateToProps } from "../types";
import { checkNewHeightAndTop, checkNewWidthAndLeft, getCenteredPaintingScrollPosition, getNewPaintingScrollPositionOnZoom, getNewPaintingScrollPrositionOnPan, getNewScaleIndex, getPaintingDimensions, gridifyDelta, isNumberBetween } from "../utils";
import { createMoveable } from "./Moveable";

export type PicassoPaintingComponentProps = {
    /** Určuje hustotu mřížky, v jaké se mají vykreslovat komponenty. Musí být celým kladným číslem. */
    readonly gridIndex: number;
    /** Přepínač určující, zdali lze označit kořenovou komponentu. */
    readonly isRootComponentSelectable: boolean;
    /** Přepínač určující, zdali je formulář designeru zobrazen jen pro čtení (např. v náhledu). */
    readonly isReadonly: boolean;
    /** Seznam komponent k vykreslení. */
    readonly components: readonly IComponent[];
    /** Označené komponenty. */
    readonly selectedComponents: readonly IComponent[];
    /** Seznam spojení k vykreslení. */
    readonly links: readonly ILink[];
    /** Označená spojení. */
    readonly selectedLinksIndexes: readonly number[];
    /** Označený bod spojení. */
    readonly selectedPoint: SelectablePoint | null;
    /** Hodnota přiblížení plátna. */
    readonly scaleIndex: number;
    /** Požadovaná pozice scrollbarů plátna picassa (k hodnotám je přičteno odsazení kořenové komponenty). */
    readonly requestedScrollPosition: ScrollPosition;
    /** Callback funkce volaná při eventu onKeyUp. */
    readonly onKeyUp: (e: KeyboardEvent<HTMLDivElement>) => void;
    /** Callback funkce volaná při eventu onKeyDown. */
    readonly onKeyDown?: (e: KeyboardEvent<HTMLDivElement>) => void;
    /** Callback funkce volaná při změně komponenty. */
    readonly onComponentChange: (changedComponentIndex: number, newX: number, newY: number, newWidth: number, newHeight: number) => void;
    readonly wrapMapStateToProps: WrapMapStateToProps<any>;
    readonly renderComponent: (componentName: string, passableProps: MoveablePassableProps) => JSX.Element;
    readonly renderLink?: (linkName: string, passableProps: LinkPassableProps) => JSX.Element;
    /** Callback funkce volaná při změně spojení. */
    readonly onLinkChange?: (changedLinkIndex: number, newPoints: readonly IPoint[]) => void;
    readonly selectComponent: (component: IComponent, reselect: boolean) => void;
    readonly unselectComponent: (componentIndex: number) => void;
    readonly updateComponents: (changedComps: readonly ComponentChangeType[]) => void;
    readonly updateLinks: (changedLinks: readonly LinkChangeType[]) => void;
    readonly changeScaleIndex: (newScaleIndex: number) => void;
    readonly unselectPoint: () => void;
} & PicassoEditorProps;

export class PicassoPaintingComponent extends Component<PicassoPaintingComponentProps> {
    public static defaultProps = {
        isRootComponentSelectable: false,
        isReadonly: false,
    };

    private isPanning = false;
    private isDragging = false;
    private ctrlKeyWhenMouseDown = false;
    private canDrag = false;
    private canResize = false;
    private initialPoint = { x: 0, y: 0 };
    private Moveable = createMoveable(this.props.wrapMapStateToProps);
    private selectedAnchors: SelectedAnchorsType | null = null;
    private picassoPaintingRef: React.RefObject<HTMLDivElement> = React.createRef();
    private picassoPaintingBoxRef: React.RefObject<HTMLDivElement> = React.createRef();
    /** Timeout for re-setting the value of 'will-change' CSS property that enables smooth scrolling on large picasso painting. */
    private willChangeAutoTimeout: number | undefined = undefined;
    /** Timeout for re-caltulating width and scroll position of picasso paintg when resize happens. */
    private resizeObserverTimeout: number | undefined = undefined;

    private resizeObserver = new ResizeObserver((e) => {
        if (e.every(x => x.contentRect.width !== 0 && x.contentRect.height !== 0)) {
            window.clearTimeout(this.resizeObserverTimeout);
            this.resizeObserverTimeout = window.setTimeout(() => {
                if (this.picassoPaintingBoxRef?.current?.parentElement == null) {
                    return;
                }

                const { width: currentWidth, height: currentHeight } = this.picassoPaintingBoxRef.current.parentElement.style;
                const { width, height } = getPaintingDimensions(this.picassoPaintingRef.current, this.props.components[0], this.props.scaleIndex);

                const [scrollByLeft, scrollByTop]: [number, number] = [
                    Number(currentWidth.replace("px", "")) - Number(width.replace("px", "")),
                    Number(currentHeight.replace("px", "")) - Number(height.replace("px", "")),
                ];

                this.picassoPaintingBoxRef.current.parentElement.style.width = width;
                this.picassoPaintingBoxRef.current.parentElement.style.height = height;

                this.picassoPaintingRef.current?.scrollBy({ left: (-scrollByLeft) / 2, top: (-scrollByTop) / 2 });
            }, 100);
        }
    });

    // Použito kvůli nefungující metodě componentDidMount, kdy MW zobrazuje formuláře v DOM-u až mnohem později,
    // než kdy se provolá componentDidMount. Proto je potřeba tohoto "hacku" v podobě IntersectionObserver-u.
    private intersectionObserver = new IntersectionObserver((e, observer) => {
        if (e.some(x => x.isIntersecting) && this.picassoPaintingRef.current && this.picassoPaintingBoxRef?.current?.parentElement) {
            const { width, height } = getPaintingDimensions(this.picassoPaintingRef.current, this.props.components[0], this.props.scaleIndex);
            this.picassoPaintingBoxRef.current.parentElement.style.width = width;
            this.picassoPaintingBoxRef.current.parentElement.style.height = height;
            this.picassoPaintingRef.current.scrollTo(getCenteredPaintingScrollPosition(this.picassoPaintingRef.current));
            this.resizeObserver.observe(this.picassoPaintingRef.current);
            observer.disconnect();
        }
    });

    constructor(props: PicassoPaintingComponentProps) {
        super(props);

        if (props.gridIndex < 1 || !Number.isInteger(props.gridIndex))
            throw new Error("Error, gridIndex must be a positive integer!");
    }

    public override render(): JSX.Element {
        const { picassoInstanceKey, isRootComponentSelectable, scaleIndex, renderComponent, renderLink, onLinkChange, wrapMapStateToProps, onKeyUp } = this.props;
        const { width, height } = getPaintingDimensions(this.picassoPaintingRef.current, this.props.components[0], this.props.scaleIndex);

        return (
            <div ref={this.picassoPaintingRef} className={PICASSO_PAINTING_CSS_CLASS} onKeyUp={onKeyUp} onKeyDown={this.handleKeyDown} onWheel={this.handleWheel} onMouseOver={this.handleMouseOver} onMouseLeave={this.handleMouseLeave} onMouseUp={this.handleMouseUp} onMouseDown={this.handleMouseDown} onMouseMove={this.handleMouseMove} onContextMenu={this.handleContextMenu} tabIndex={TAB_INDEX_FOCUSABLE_TAB_SEQUENCE}>
                <div className={PICASSO_PAINTING_WRAPPER_CSS_CLASS} style={{ width, height }}>
                    <div ref={this.picassoPaintingBoxRef} className={PICASSO_PAINTING_BOX_CSS_CLASS} style={{
                        scale: scaleIndex.toString(), // toString() because of bug in our version of React https://github.com/facebook/react/pull/25601
                        width: this.props.components[0] ? (this.props.components[0].width + this.props.components[0].relativeLeft) : 0,
                        height: this.props.components[0] ? (this.props.components[0].height + this.props.components[0].relativeTop) : 0,
                    }}>
                        {this.props.components.length && <this.Moveable
                            index={CONST_ROOT_COMPONENT_INDEX}
                            isSelectable={isRootComponentSelectable}
                            isResizable={false}
                            isDraggable={false}
                            onResizePointDown={this.handleComponentsResizePointDown}
                            onMouseUp={this.handleComponentMouseUp}
                            onMouseClick={this.handleComponentClick}
                            onMouseLeave={this.handleComponentMouseLeave}
                            onMouseDown={this.handleComponentMouseDown}
                            onMouseMove={this.handleComponentMouseMove}
                            {...{ renderComponent, renderLink, onLinkChange, wrapMapStateToProps, picassoInstanceKey }} />}
                    </div>
                </div>
            </div>
        );
    }

    public override componentDidUpdate(prevProps: Readonly<PicassoPaintingComponentProps>, _prevState: Readonly<EmptyObjectType>, _snapshot?: any): void {
        if (prevProps.requestedScrollPosition !== this.props.requestedScrollPosition && this.picassoPaintingRef.current) {
            const { left, top } = getCenteredPaintingScrollPosition(this.picassoPaintingRef.current);
            this.picassoPaintingRef.current.scrollTo({ left: this.props.requestedScrollPosition.left + left, top: this.props.requestedScrollPosition.top + top, behavior: "smooth" });
        }
    }

    public override componentDidMount(): void {
        if (this.picassoPaintingRef.current) {
            this.intersectionObserver.observe(this.picassoPaintingRef.current);
        }
    }

    public override componentWillUnmount(): void {
        this.intersectionObserver.disconnect();
        this.resizeObserver.disconnect();
        clearTimeout(this.resizeObserverTimeout);
    }

    //#region Handlování eventů na Picasso Painting

    /** Zabraňuje vyskočení kontextového menu po right-clicku */
    private handleContextMenu = (e: MouseEvent) => {
        e.preventDefault();
    };

    /** Po mouse-upu se musí vypnout pannování plátna */
    private handleMouseUp = () => {
        this.isPanning = false;
    };

    private preventBrowserZoom = (e: globalThis.WheelEvent) => {
        if (e.ctrlKey) {
            e.preventDefault();
        }
    };

    private preventMouseMiddleButtonScroll = (e: globalThis.MouseEvent) => {
        if (e.button === 1) {
            e.preventDefault();
        }
    };

    /** Pokud se na plátno klikne pravým myšítkem (nebo scrollwheelem) zapne se pannování plátna a nastaví se počáteční bod pannování */
    private handleMouseDown = (e: MouseEvent) => {
        if (e.buttons !== CONST_RIGHT_MOUSE_BTN && e.buttons !== CONST_MIDDLE_MOUSE_BTN || !this.picassoPaintingRef.current) {
            return;
        }

        this.initialPoint = { x: e.clientX + this.picassoPaintingRef.current.scrollLeft, y: e.clientY + this.picassoPaintingRef.current.scrollTop };
        this.isPanning = true;
    };

    /** Pokud je pannování zapnuto, tak se při pohybu kurzoru nad plátnem vyvolává pannování */
    private handleMouseMove = (e: MouseEvent) => {
        if (!this.isPanning || (e.buttons !== CONST_RIGHT_MOUSE_BTN && e.buttons !== CONST_MIDDLE_MOUSE_BTN))
            return;

        const newScrollPosition = getNewPaintingScrollPrositionOnPan(this.initialPoint, e);
        this.picassoPaintingRef.current?.scrollTo(newScrollPosition);
    };

    /** Při protočení kolečka myši dochází k oddálení/přblížení plátna */
    private handleWheel = (e: WheelEvent) => {
        if (e.buttons !== CONST_NO_MOUSE_BTN || !e.ctrlKey || !this.picassoPaintingRef.current || !this.picassoPaintingBoxRef?.current?.parentElement) {
            return;
        }

        const { scaleIndex, changeScaleIndex } = this.props;
        const newScaleIndex = getNewScaleIndex(e, scaleIndex);

        if (newScaleIndex > CONST_MAX_SCALE_INDEX || newScaleIndex < CONST_MIN_SCALE_INDEX)
            return;

        window.clearTimeout(this.willChangeAutoTimeout);
        this.picassoPaintingBoxRef.current.style.willChange = "scale";

        // Kromě změny scale indexu je i nutné vypočítat i nový scroll position plátna, aby se posunulo tam, kam míří kurzor.

        const newPaintingPosition = getNewPaintingScrollPositionOnZoom(e, this.picassoPaintingRef.current, scaleIndex, newScaleIndex);
        const { width, height } = getPaintingDimensions(this.picassoPaintingRef.current, this.props.components[0], newScaleIndex);
        this.picassoPaintingBoxRef.current.style.scale = newScaleIndex.toString();
        this.picassoPaintingBoxRef.current.parentElement.style.height = height;
        this.picassoPaintingBoxRef.current.parentElement.style.width = width;
        this.picassoPaintingRef.current.scrollTo(newPaintingPosition);
        changeScaleIndex(newScaleIndex);

        this.willChangeAutoTimeout = window.setTimeout(() => {
            if (this.picassoPaintingBoxRef.current) {
                this.picassoPaintingBoxRef.current.style.willChange = "auto";
            }
        }, 100);
    };

    private handleMouseOver = () => {
        window.addEventListener("wheel", this.preventBrowserZoom, { passive: false });
        window.addEventListener("mousedown", this.preventMouseMiddleButtonScroll, { passive: false });
    };

    private handleMouseLeave = () => {
        window.removeEventListener("wheel", this.preventBrowserZoom, false);
        window.removeEventListener("mousedown", this.preventMouseMiddleButtonScroll, false);
    };

    /** Obstarává posun picasso komponent pomocí směrových šipek na klávesnici. */
    private handleKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
        const { isReadonly, selectedComponents, gridIndex, onKeyDown } = this.props;

        onKeyDown && onKeyDown(e);

        if (PAINTINGS_PREVENTED_KEYS.includes(e.key)) {
            e.preventDefault();
        }

        if (isReadonly || selectedComponents.length === 0 || selectedComponents.some(x => x.align !== AlignType.none))
            return;

        const stepSize = e.shiftKey ? 1 : gridIndex;

        if (e.key === CONST_ARROW_UP) {
            this.updateComponentsAndLinks(0, -stepSize);
        } else if (e.key === CONST_ARROW_DOWN) {
            this.updateComponentsAndLinks(0, stepSize);
        } else if (e.key === CONST_ARROW_LEFT) {
            this.updateComponentsAndLinks(-stepSize, 0);
        } else if (e.key === CONST_ARROW_RIGHT) {
            this.updateComponentsAndLinks(stepSize, 0);
        }
    };

    ////#endregion

    //#region Handlování eventů na Moveable componentách

    /** Při Mouse leave dojde k vypnutí dragování a resizování */
    private handleComponentMouseLeave = (e: MouseEvent) => {
        if (e.buttons === CONST_NO_MOUSE_BTN) {
            this.canDrag = false;
            this.canResize = false;
        }
    };

    private handleComponentMouseUp = (e: MouseEvent, _uiComponent: IComponent) => {
        const { selectedPoint, links, components } = this.props;

        if (selectedPoint == null)
            return;

        const linkToChange = links[selectedPoint.linkIndex];

        /* PROSÍM NEMAZAT! PŘIPRAVENO PRO PODPORU CHANGOVÁNÍ COMPONENT LINKU
        if (uiComponent.id !== linkToChange.ownerId) {
            if (uiComponent.id === linkToChange.endComponentId || uiComponent.id === linkToChange.startComponentId) //todo nebude fungovat, když budu chtít dát link na komponentu, která bude linkovat sama sebe
                return;

            if (selectedPoint.pointIndex === linkToChange.points.length - 1) {
                Tady je prostor pro callback onLinksComponentChange, kdysi tu byla celá složitá logika, ale asi to nemá moc smysl
                e.stopPropagation();
                return;
            }

            if (selectedPoint.pointIndex === 0) {
                Tady je prostor pro callback onLinksComponentChange, kdysi tu byla celá složitá logika, ale asi to nemá moc smysl
                e.stopPropagation();
                return;
            }
        }*/

        let relatedComponentId: number;

        if (selectedPoint.pointIndex === linkToChange.points.length - 1)
            relatedComponentId = linkToChange.endComponentIndex;
        else if (selectedPoint.pointIndex === 0)
            relatedComponentId = linkToChange.startComponentIndex;
        else
            return;

        const relatedComponent = components[relatedComponentId];
        const point = linkToChange.points[selectedPoint.pointIndex];
        const isPointWithinComponent = isNumberBetween(point.x, relatedComponent.relativeLeft, relatedComponent.relativeLeft + relatedComponent.width)
            && isNumberBetween(point.y, relatedComponent.relativeTop, relatedComponent.relativeTop + relatedComponent.height);

        if (isPointWithinComponent)
            return;

        const deltaLeft = selectedPoint.lastPosition.x - point.x;
        const deltaTop = selectedPoint.lastPosition.y - point.y;
        const newPoints = this.changePointPosition(linkToChange, selectedPoint.pointIndex, deltaLeft, deltaTop);

        this.props.updateLinks([{ index: selectedPoint.linkIndex, newPoints }]);

        if (this.props.onLinkChange)
            this.props.onLinkChange(linkToChange.index, newPoints);

        e.stopPropagation();
    };

    private handleComponentMouseMove = (e: MouseEvent, uiComponent: IComponent, isSelected: boolean) => {
        const { isReadonly, selectedPoint, selectedComponents, links } = this.props;

        if (isReadonly)
            return;

        if (selectedPoint != null) {
            this.dragPoint(e, selectedPoint, links[selectedPoint.linkIndex], uiComponent.index);
            return;
        }

        if (isSelected || (selectedComponents.length < 0 || selectedComponents.some(selComp => selComp.parent !== uiComponent.index)))
            return;

        if (this.canResize) {
            this.resizeComponent(e);
            return;
        } else if (this.canDrag) {
            this.dragComponent(e);
            return;
        }
    };

    /** Dojde k označení/odznačení komponenty */
    private handleComponentClick = (e: MouseEvent, component: IComponent, isSelected: boolean, isSelectable: boolean) => {
        if (isSelectable && this.ctrlKeyWhenMouseDown === e.ctrlKey) {
            if (isSelected && !e.ctrlKey && !this.isDragging)
                this.props.selectComponent(component, true);
            else if (!isSelected && e.ctrlKey && !this.isDragging)
                this.props.selectComponent(component, false);
            else if (isSelected && e.ctrlKey && !this.isDragging)
                this.props.unselectComponent(component.index);
        }

        e.stopPropagation();
    };

    private handleComponentMouseDown = (e: MouseEvent, isSelected: boolean, isDraggable: boolean, isSelectable: boolean, component: IComponent) => {
        this.ctrlKeyWhenMouseDown = e.ctrlKey;

        if (e.buttons !== CONST_LEFT_MOUSE_BTN || !this.picassoPaintingBoxRef.current) {
            return;
        }

        this.isDragging = false;

        if (!isSelected && !e.ctrlKey && isSelectable)
            this.props.selectComponent(component, true);

        e.stopPropagation();

        if (!isDraggable) {
            this.canResize = false;
            this.canDrag = false;
            return;
        }

        const { left, top } = this.picassoPaintingBoxRef.current.getBoundingClientRect();

        this.canResize = false;
        this.canDrag = isSelectable;
        this.initialPoint.x = e.clientX - left;
        this.initialPoint.y = e.clientY - top;
    };

    private handleComponentsResizePointDown = (e: MouseEvent, selectedAnchors: SelectedAnchorsType) => {
        this.canResize = true;
        this.canDrag = false;
        this.selectedAnchors = selectedAnchors;

        e.stopPropagation();
    };

    //#endregion

    private dragPoint = (e: MouseEvent, selectedPoint: SelectablePoint, linkToChange: ILink, componentId: number) => {
        if (linkToChange.ownerIndex !== componentId)
            return;

        if (linkToChange.ownerIndex !== componentId || e.buttons !== CONST_LEFT_MOUSE_BTN) {
            this.props.unselectPoint();
            return;
        }

        const { scaleIndex, updateLinks, onLinkChange } = this.props;
        const newPoints = [...linkToChange.points];

        newPoints[selectedPoint.pointIndex] = {
            x: ((e.clientX - selectedPoint.originCursorCoordinates.x) / scaleIndex) + selectedPoint.lastPosition.x,
            y: ((e.clientY - selectedPoint.originCursorCoordinates.y) / scaleIndex) + selectedPoint.lastPosition.y,
        };

        updateLinks([{ index: selectedPoint.linkIndex, newPoints }]);

        if (onLinkChange)
            onLinkChange(linkToChange.index, newPoints);

        e.stopPropagation();
    };

    private dragComponent = (e: MouseEvent) => {
        if (e.buttons !== CONST_LEFT_MOUSE_BTN || e.ctrlKey || !this.picassoPaintingBoxRef.current) {
            return;
        }

        this.isDragging = true;
        const { scaleIndex, gridIndex } = this.props;

        const { left, top } = this.picassoPaintingBoxRef.current.getBoundingClientRect();
        const x = e.clientX - left;
        const y = e.clientY - top;

        const rawDeltaLeft = (x - this.initialPoint.x) / scaleIndex;
        const deltaLeft = gridifyDelta(rawDeltaLeft, gridIndex);

        const rawDeltaTop = (y - this.initialPoint.y) / scaleIndex;
        const deltaTop = gridifyDelta(rawDeltaTop, gridIndex);

        if (deltaLeft !== 0 || deltaTop !== 0) {
            if (deltaLeft !== 0) {
                this.initialPoint.x = x - (rawDeltaLeft - deltaLeft) * scaleIndex;
            }

            if (deltaTop !== 0) {
                this.initialPoint.y = y - (rawDeltaTop - deltaTop) * scaleIndex;
            }

            this.updateComponentsAndLinks(deltaLeft, deltaTop);
        }

        e.stopPropagation();
    };

    private resizeComponent = (e: MouseEvent) => {
        if (e.buttons !== CONST_LEFT_MOUSE_BTN || this.selectedAnchors === null)
            return;

        const gridIndex = this.props.gridIndex;

        let deltaWidth, deltaHeight, deltaLeft, deltaTop = 0;

        const nRect = this.selectedAnchors.nAnchor.element.getBoundingClientRect();
        const neRect = this.selectedAnchors.neAnchor.element.getBoundingClientRect();
        const eRect = this.selectedAnchors.eAnchor.element.getBoundingClientRect();
        const seRect = this.selectedAnchors.seAnchor.element.getBoundingClientRect();
        const sRect = this.selectedAnchors.sAnchor.element.getBoundingClientRect();
        const swRect = this.selectedAnchors.swAnchor.element.getBoundingClientRect();
        const wRect = this.selectedAnchors.wAnchor.element.getBoundingClientRect();
        const nwRect = this.selectedAnchors.nwAnchor.element.getBoundingClientRect();

        if (this.selectedAnchors.nAnchor.isSelected) {
            if (e.clientY <= sRect.top) {
                deltaHeight = gridifyDelta(nRect.top - e.clientY, gridIndex);
                deltaTop = gridifyDelta(-deltaHeight, gridIndex);
            }
        } else if (this.selectedAnchors.neAnchor.isSelected) {
            if (e.clientX >= swRect.left && e.clientY <= swRect.bottom) {
                deltaWidth = gridifyDelta(e.clientX - neRect.left, gridIndex);
                deltaHeight = gridifyDelta(neRect.top - e.clientY, gridIndex);
                deltaTop = gridifyDelta(-deltaHeight, gridIndex);
            }
        } else if (this.selectedAnchors.eAnchor.isSelected) {
            if (e.clientX >= wRect.left) {
                deltaWidth = gridifyDelta(e.clientX - eRect.left, gridIndex);
            }
        } else if (this.selectedAnchors.seAnchor.isSelected) {
            if (e.clientX >= nwRect.left && e.clientY >= nwRect.top) {
                deltaWidth = gridifyDelta(e.clientX - seRect.left, gridIndex);
                deltaHeight = gridifyDelta(e.clientY - seRect.bottom, gridIndex);
            }
        } else if (this.selectedAnchors.sAnchor.isSelected) {
            if (e.clientY >= nRect.top) {
                deltaHeight = gridifyDelta(e.clientY - sRect.top, gridIndex);
            }
        } else if (this.selectedAnchors.swAnchor.isSelected) {
            if (e.clientX <= neRect.right && e.clientY >= neRect.bottom) {
                deltaWidth = gridifyDelta(swRect.right - e.clientX, gridIndex);
                deltaHeight = gridifyDelta(e.clientY - swRect.top, gridIndex);
                deltaLeft = gridifyDelta(-deltaWidth, gridIndex);
            }
        } else if (this.selectedAnchors.wAnchor.isSelected) {
            if (e.clientX <= eRect.left) {
                deltaWidth = gridifyDelta(wRect.left - e.clientX, gridIndex);
                deltaLeft = gridifyDelta(-deltaWidth, gridIndex);
            }
        } else if (this.selectedAnchors.nwAnchor.isSelected) {
            if (e.clientX <= seRect.right && e.clientY <= seRect.bottom) {
                deltaWidth = gridifyDelta(nwRect.right - e.clientX, gridIndex);
                deltaHeight = gridifyDelta(nwRect.bottom - e.clientY, gridIndex);
                deltaLeft = gridifyDelta(-deltaWidth, gridIndex);
                deltaTop = gridifyDelta(-deltaHeight, gridIndex);
            }
        }

        if (deltaWidth || deltaHeight || deltaLeft || deltaTop)
            this.updateComponentsAndLinks(deltaLeft, deltaTop, deltaWidth, deltaHeight);

        e.stopPropagation();
    };

    private changePointPosition = (link: ILink, pointIndex: number, deltaLeft: number, deltaTop: number): IPoint[] => {
        const newX = link.points[pointIndex].x + deltaLeft;
        const newY = link.points[pointIndex].y + deltaTop;

        const newPoints = [...link.points];
        newPoints[pointIndex] = { x: newX, y: newY };

        return newPoints;
    };

    private updateComponentsAndLinks = (deltaLeft = 0, deltaTop = 0, deltaWidth = 0, deltaHeight = 0) => {
        const { selectedComponents, selectedLinksIndexes, links, onComponentChange, onLinkChange } = this.props;
        const changedComps: ComponentChangeType[] = [];
        const changedLinks: LinkChangeType[] = [];

        for (let i = 0; i < selectedComponents.length; i++) {
            const selectedComponent = selectedComponents[i];

            // change of comp
            const widthAndLeft = checkNewWidthAndLeft(selectedComponent.width + deltaWidth, selectedComponent.relativeLeft + deltaLeft, deltaLeft, selectedComponent);
            const heightAndTop = checkNewHeightAndTop(selectedComponent.height + deltaHeight, selectedComponent.relativeTop + deltaTop, deltaTop, selectedComponent);

            if (widthAndLeft.left < 0 || heightAndTop.top < 0)
                return;

            changedComps.push({
                componentIndex: selectedComponent.index,
                x: widthAndLeft.left,
                y: heightAndTop.top,
                width: widthAndLeft.width,
                height: heightAndTop.height,
                deltaX: widthAndLeft.left - selectedComponent.relativeLeft,
                deltaY: heightAndTop.top - selectedComponent.relativeTop,
            });

            // change of link
            for (let j = 0; j < links.length; j++) {
                const { index, startComponentIndex, points, endComponentIndex } = links[j];

                const isLinkSelected = selectedLinksIndexes.includes(index);
                const isStartComponentAlsoSelected = selectedComponents.some(selectedComp => selectedComp.index === startComponentIndex);

                const alreadyChangedLink = changedLinks.find(changedLink => changedLink.index === j);
                const newPoints = alreadyChangedLink == null ? [...points] : [...alreadyChangedLink.newPoints];

                if (selectedComponent.index === startComponentIndex) {
                    const isEndComponentAlsoSelected = selectedComponents.some(selectedComp => selectedComp.index === endComponentIndex);

                    if (isEndComponentAlsoSelected && isLinkSelected) {
                        for (let k = 0; k < newPoints.length; k++) {
                            newPoints[k] = { x: newPoints[k].x + deltaLeft, y: newPoints[k].y + deltaTop };
                        }
                    } else
                        newPoints[0] = { x: newPoints[0].x + deltaLeft, y: newPoints[0].y + deltaTop };

                    changedLinks.push({ index: j, newPoints });
                }

                if (selectedComponent.index === endComponentIndex && (!isLinkSelected || isLinkSelected && !isStartComponentAlsoSelected)) {
                    newPoints[newPoints.length - 1] = {
                        x: newPoints[newPoints.length - 1].x + deltaLeft,
                        y: newPoints[newPoints.length - 1].y + deltaTop,
                    };

                    changedLinks.push({ index: j, newPoints });
                }
            }
        }

        if (changedComps.length > 0) {
            this.props.updateComponents(changedComps);
            changedComps.forEach(changedComponent => onComponentChange(changedComponent.componentIndex, changedComponent.x, changedComponent.y, changedComponent.width, changedComponent.height));
        }

        if (changedLinks.length > 0) {
            this.props.updateLinks(changedLinks);

            if (onLinkChange)
                changedLinks.forEach(changedLink => onLinkChange(changedLink.index, changedLink.newPoints));
        }
    };
}
